import { useState } from 'react';
import { useBulkAssign, useCaseManagers } from '../api/assignments.ts';
import { ApiError } from '../api/client.ts';

/**
 * Assign a batch of clients from the visits board.
 *
 * Shown only on the "No SW case manager" card, which is where the backlog is
 * actually noticed, and only to someone the server said may assign.
 *
 * **It assigns what was ticked, never "all of them".** The board is scrolled in
 * batches, so "all" would mean either the rows loaded — a number that depends
 * on how far somebody happened to scroll — or a set nobody has looked at. Three
 * hundred clients given to one case manager by a single click, off a count on
 * screen, is not a decision anyone would make deliberately.
 */
export function BulkAssignBar({
  picked, total, onClear, onDone,
}: {
  picked: string[];
  total: number;
  onClear: () => void;
  onDone: (assigned: number) => void;
}) {
  const [staffId, setStaffId] = useState('');
  const managers = useCaseManagers('Social Work', true);
  const assign = useBulkAssign();

  return (
    <div className="sticky top-0 z-20 flex flex-wrap items-center gap-2 border-b border-sky-200
                    bg-sky-50 px-4 py-3 lg:px-5">
      <p className="text-[0.9375rem] font-semibold text-neutral-900">
        {picked.length === 0
          ? `${total} ${total === 1 ? 'client has' : 'clients have'} no case manager`
          : `${picked.length} selected`}
      </p>

      {picked.length > 0 && (
        <>
          <label htmlFor="bulk-assign-to" className="sr-only">Assign to</label>
          <select
            id="bulk-assign-to"
            value={staffId}
            onChange={(e) => setStaffId(e.target.value)}
            className="min-h-[44px] rounded-lg border border-neutral-300 bg-white px-2
                       text-[0.9375rem] text-neutral-800"
          >
            <option value="">Assign to…</option>
            {(managers.data?.case_managers ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.full_name} · {m.caseload} {m.caseload === 1 ? 'client' : 'clients'}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!staffId || assign.isPending}
            onClick={() =>
              assign.mutate(
                { client_ids: picked, staff_user_id: staffId, department: 'Social Work' },
                {
                  onSuccess: (r) => { setStaffId(''); onDone(r.assigned); },
                },
              )
            }
            className="min-h-[44px] rounded-lg bg-neutral-900 px-4 text-[0.9375rem] font-semibold
                       text-white disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed"
          >
            {assign.isPending ? 'Assigning…' : `Assign ${picked.length}`}
          </button>
          <button
            type="button"
            onClick={onClear}
            className="min-h-[44px] rounded-lg border border-neutral-300 bg-white px-3
                       text-[0.9375rem] font-semibold text-neutral-800"
          >
            Clear
          </button>
        </>
      )}

      {picked.length === 0 && (
        <p className="text-sm text-neutral-600">Tick the clients to give to a case manager.</p>
      )}

      {assign.error && (
        <p className="w-full rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-900">
          {assign.error.message}
          {assign.error instanceof ApiError && assign.error.hint && (
            <span className="block text-rose-700">{assign.error.hint}</span>
          )}
        </p>
      )}
    </div>
  );
}
